// @flow
import React from 'react';
import PhoneInput from 'react-phone-number-input';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

// Normalizes display-formatted phone strings (e.g. "+1 212 542 0942") to E.164
// so `react-phone-number-input` doesn't warn about non-E.164 `value`.
//
// Only strings containing whitespace are normalized. Values emitted by the
// library's own `onChange` are already E.164 — including *partial* values
// while the user is typing (e.g. "+12") — and must pass through unchanged;
// forcing those through the parser turns partials into undefined, which makes
// the library discard the in-progress keystroke on the next render.
export const toE164 = (v: ?string): ?string => {
  if (!v || typeof v !== 'string') return v;
  if (!/\s/.test(v)) return v;
  const parsed = parsePhoneNumberFromString(v);
  return parsed ? parsed.number : v;
};

// Create a styled PhoneInput component that matches MUI Input styling
const StyledPhoneInputContainer = styled('div')(({ theme, variant = 'standard' }) => ({
  ...(variant === 'standard' && {
    marginBottom: theme.spacing(1)
  }),
  position: 'relative',
  width: '100%',
  boxSizing: 'border-box',

  // Main PhoneInput container
  '& .PhoneInput': {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box',

    // Standard variant (underline)
    ...(variant === 'standard' && {
      borderBottom: `1px solid rgba(0, 0, 0, 0.57)`,
      transition: 'border-bottom-color 0.2s ease-in-out',

      '&:hover': {
        borderBottom: `2px solid ${theme.palette.border.color}`
      },

      '&.PhoneInput--focus': {
        borderBottom: `2px solid ${theme.palette.primary.main}`
      }
    }),

    // Outlined variant (border all around)
    ...(variant === 'outlined' && {
      border: '1px solid rgba(0, 0, 0, 0.87)',
      borderRadius: '4px',
      padding: '1.5rem 14px',
      transition: 'border-color 0.2s ease-in-out',

      '&:hover': {
        borderColor: 'rgba(0, 0, 0, 0.87)'
      },

      '&.PhoneInput--focus': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
        padding: 'calc(1.5rem - 1px) 13px' // Adjust padding for 2px border
      }
    })
  },

  // Country selector container
  '& .PhoneInputCountry': {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    ...(variant === 'standard' && {
      paddingBottom: theme.spacing(0.5)
    }),
    cursor: 'pointer'
  },

  // Flag icon
  '& .PhoneInputCountryIcon': {
    width: '1.5em',
    height: '1em',
    borderRadius: 2,
    boxShadow: `0 0 0 1px ${theme.palette.divider}`
  },

  // Country selector dropdown
  '& .PhoneInputCountrySelect': {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    zIndex: 1,
    border: 0,
    opacity: 0,
    cursor: 'pointer',
    
    '&:focus + .PhoneInputCountryIcon': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
    }
  },

  // Dropdown arrow
  '& .PhoneInputCountrySelectArrow': {
    display: 'block',
    width: '0.3em',
    height: '0.3em',
    marginLeft: theme.spacing(0.5),
    borderStyle: 'solid',
    borderColor: theme.palette.text.secondary,
    borderTopWidth: 0,
    borderBottomWidth: '1px',
    borderLeftWidth: 0,
    borderRightWidth: '1px',
    transform: 'rotate(45deg)',
    opacity: 0.7,
    transition: 'all 0.2s ease-in-out'
  },

  // Phone number input field
  '& .PhoneInputInput': {
    flex: 1,
    minWidth: 0,
    border: 0,
    outline: 0,
    backgroundColor: 'transparent',
    fontSize: '1rem',
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    ...(variant === 'outlined' && {
      paddingTop: 0,
      paddingBottom: 0
    }),
    ...(variant === 'standard' && {
      paddingBottom: theme.spacing(0.5),
      paddingTop: theme.spacing(0.5)
    }),

    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.6
    },

    '&:disabled': {
      color: theme.palette.text.disabled,
      cursor: 'default'
    }
  },

  // Error state
  '&.error': {
    '& .PhoneInput': {
      borderBottom: `2px solid ${theme.palette.error.main}`,
      
      '&:hover': {
        borderBottom: `2px solid ${theme.palette.error.main}`
      }
    }
  },

  // Disabled state
  '&.disabled': {
    opacity: 0.6,
    pointerEvents: 'none'
  }
}));

type Props = {
  value?: string,
  onChange: (value: string) => void,
  placeholder?: string,
  defaultCountry?: string,
  disabled?: boolean,
  error?: boolean,
  variant?: 'standard' | 'outlined',
  className?: string,
  id?: string
};

const StyledPhoneInput = ({
  value,
  onChange,
  placeholder = "Enter phone number",
  defaultCountry = "US",
  disabled = false,
  error = false,
  variant = 'standard',
  className = "",
  ...props
}: Props) => {
  const theme = useTheme();

  return (
    <StyledPhoneInputContainer
      className={`${className} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
      variant={variant}
    >
      <PhoneInput
        value={toE164(value)}
        onChange={onChange}
        defaultCountry={defaultCountry}
        placeholder={placeholder}
        disabled={disabled}
        {...props}
      />
    </StyledPhoneInputContainer>
  );
};

export default StyledPhoneInput;





